import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export type OutlineNodeType =
  | 'part'
  | 'chapter'
  | 'scene'
  | 'section'
  | 'custom';
export type OutlineNodeStatus =
  | 'not_started'
  | 'in_progress'
  | 'drafted'
  | 'revised'
  | 'complete';
export type HierarchyPreset =
  | 'novel'
  | 'screenplay'
  | 'short_story'
  | 'series'
  | 'video'
  | 'custom';

export interface LinkedEntityRef {
  entityId: string;
  role: string;
  note?: string;
}

export interface OutlineNodeDto {
  id: string;
  outlineId: string;
  parentId?: string | null;
  name: string;
  description?: string | null;
  sequenceOrder: number;
  linkedChapterId?: string | null;
  nodeType?: OutlineNodeType | null;
  synopsis?: string | null;
  location?: string | null;
  timePeriod?: string | null;
  mood?: string | null;
  tone?: string | null;
  estimatedWordCount?: number | null;
  status?: OutlineNodeStatus | null;
  colorLabel?: string | null;
  notes?: string | null;
  povCharacterId?: string | null;
  sceneType?: string | null;
  scenePurpose?: string | null;
  sceneStart?: string | null;
  sceneEnd?: string | null;
  emotionalBeat?: string | null;
  chronologicalOrder?: number | null;
}

export interface OutlineDto {
  id: string;
  projectId: string;
  name: string;
  structureId?: string | null;
  hierarchyPreset?: HierarchyPreset | null;
  nodes: OutlineNodeDto[];
  updatedAt: string;
}

export interface OutlineCharacterDto {
  id: string;
  outlineId: string;
  entityId: string;
  entityName?: string | null;
  role?: string | null;
  customRoleName?: string | null;
  goal?: string | null;
  goalType?: string | null;
  motivation?: string | null;
  stakes?: string | null;
  arcSummary?: string | null;
  sequenceOrder: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export function useOutlinesForBook(projectId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<OutlineDto[]>({
    enabled: !!projectId,
    queryKey: ['outlines', projectId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/projects/${projectId}/outlines`)
        .json<ApiEnvelope<OutlineDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useCreateOutline() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId?: string;
      name: string;
      structureId?: string | null;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post('api/v2/outlines', {
          json: { ...input, projectId: input.projectId ?? null },
        })
        .json<ApiEnvelope<OutlineDto>>();
      return resp.data;
    },
    onSuccess: (_, vars) => {
      if (vars.projectId) {
        void qc.invalidateQueries({ queryKey: ['outlines', vars.projectId] });
      }
      void qc.invalidateQueries({ queryKey: ['outlines-all'] });
    },
  });
}

export function useAddOutlineNode(bookId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      outlineId: string;
      parentId?: string | null;
      name: string;
      description?: string;
    }) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/outlines/${input.outlineId}/nodes`, {
        json: {
          parentId: input.parentId,
          name: input.name,
          description: input.description,
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outlines', bookId] }),
  });
}

export function useDeleteOutlineNode(bookId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/outline-nodes/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outlines', bookId] }),
  });
}

export function usePromoteOutlineToChapters(bookId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (outlineId: string) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/outlines/${outlineId}/promote`)
        .json<ApiEnvelope<string[]>>();
      return resp.data ?? [];
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['outlines', bookId] });
      void qc.invalidateQueries({ queryKey: ['chapters', bookId] });
    },
  });
}

export function useDeleteOutline() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/outlines/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['outlines-all'] });
    },
  });
}

// --- Get single outline by ID ---

export function useOutlineById(outlineId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<OutlineDto | null>({
    enabled: !!outlineId,
    queryKey: ['outline-by-id', outlineId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/outlines/${outlineId}`)
        .json<ApiEnvelope<OutlineDto>>();
      return resp.data;
    },
  });
}

// --- Standalone mutation hooks (outlineId-keyed invalidation) ---

export function useAddOutlineNodeStandalone(outlineId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      outlineId: string;
      parentId?: string | null;
      name: string;
      description?: string;
    }) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/outlines/${input.outlineId}/nodes`, {
        json: {
          parentId: input.parentId,
          name: input.name,
          description: input.description,
        },
      });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outline-by-id', outlineId] }),
  });
}

export function useDeleteOutlineNodeStandalone(outlineId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/outline-nodes/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outline-by-id', outlineId] }),
  });
}

// --- Standalone listing ---

export interface OutlineSummaryDto {
  id: string;
  projectId?: string | null;
  projectTitle?: string | null;
  name: string;
  nodeCount: number;
  updatedAt: string;
}

export function useMyOutlines() {
  const { getToken } = useAuth();
  return useQuery<OutlineSummaryDto[]>({
    queryKey: ['outlines-all'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/outlines')
        .json<ApiEnvelope<OutlineSummaryDto[]>>();
      return resp.data ?? [];
    },
  });
}

// --- Update outline node (all fields) ---

export function useUpdateOutlineNode(outlineId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      description?: string | null;
      nodeType?: string | null;
      synopsis?: string | null;
      location?: string | null;
      timePeriod?: string | null;
      mood?: string | null;
      tone?: string | null;
      estimatedWordCount?: number | null;
      status?: string | null;
      colorLabel?: string | null;
      notes?: string | null;
      povCharacterId?: string | null;
      sceneType?: string | null;
      scenePurpose?: string | null;
      sceneStart?: string | null;
      sceneEnd?: string | null;
      emotionalBeat?: string | null;
      chronologicalOrder?: number | null;
    }) => {
      const { id, ...body } = input;
      const client = createApiClient(getToken);
      const resp = await client
        .put(`api/v2/outline-nodes/${id}`, { json: body })
        .json<ApiEnvelope<OutlineNodeDto>>();
      return resp.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['outline-by-id', outlineId] });
      void qc.invalidateQueries({ queryKey: ['outlines-all'] });
    },
  });
}

// --- Outline characters ---

export function useOutlineCharacters(outlineId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<OutlineCharacterDto[]>({
    enabled: !!outlineId,
    queryKey: ['outline-characters', outlineId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/outlines/${outlineId}/characters`)
        .json<ApiEnvelope<OutlineCharacterDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useAddOutlineCharacter(outlineId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      entityId: string;
      role?: string | null;
      customRoleName?: string | null;
      goal?: string | null;
      goalType?: string | null;
      motivation?: string | null;
      stakes?: string | null;
      arcSummary?: string | null;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/outlines/${outlineId}/characters`, { json: input })
        .json<ApiEnvelope<OutlineCharacterDto>>();
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outline-characters', outlineId] }),
  });
}

export function useRemoveOutlineCharacter(outlineId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/outline-characters/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outline-characters', outlineId] }),
  });
}

// --- Project-scoped outline mutations ---

export function useDuplicateOutline() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      outlineId: string;
      projectId: string;
      newLabel?: string;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/outlines/${input.outlineId}/duplicate`, {
          json: { newLabel: input.newLabel },
        })
        .json<ApiEnvelope<OutlineDto>>();
      return { data: resp.data, projectId: input.projectId };
    },
    onSuccess: ({ projectId }) => {
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['outlines-all'] });
    },
  });
}

export function useSetPrimaryOutline() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { outlineId: string; projectId: string }) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/outlines/${input.outlineId}/set-primary`);
      return input;
    },
    onSuccess: ({ projectId }) => {
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useRenameOutline() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      outlineId: string;
      projectId: string;
      label: string;
      stage?: string;
    }) => {
      const client = createApiClient(getToken);
      await client.patch(`api/v2/outlines/${input.outlineId}/label`, {
        json: { label: input.label, stage: input.stage },
      });
      return input;
    },
    onSuccess: ({ projectId, outlineId }) => {
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['outline-by-id', outlineId] });
      void qc.invalidateQueries({ queryKey: ['outlines-all'] });
    },
  });
}
